import React, { useEffect, useRef } from "react";
import { useScrollToTop } from "../../hooks";
import { MGPageToolbar, TMGPageToolbarProps } from "../MGPageToolbar/MGPageToolbar";
import { useFocusedFieldScroll } from "./useFocusedFieldScroll";
import styles from "./MGPage.module.css";

type MGPageProps = {
  /** Main content rendered inside the page. */
  children: React.ReactNode;
  /** Resets the page scroll position when this value changes. */
  scrollToTopKey?: React.Key;
  /** Configures the optional toolbar, including its title, actions, and progress. */
  toolbar?: TMGPageToolbarProps;
  /** Identifier used to locate the page in tests. */
  dataTestId?: string;
  /** Additional CSS class applied to the page root container. */
  className?: string;
  /** Additional CSS class applied to the scroll container. */
  bodyClassName?: string;
  /** Additional CSS class applied to the inner content container. */
  contentClassName?: string;
  /** Top space reserved for a fixed toolbar; overrides the automatic calculation. */
  fixedToolbarOffset?: number;
  /** Additional inline styles applied to the page root container. */
  style?: React.CSSProperties;
  /** Enables internal scrolling and focus adjustments for the authenticated WebView. */
  isMobileAuth: boolean;
};

const TOOLBAR_HEIGHT = 56;

function getClassName(...classes: Array<string | undefined | false>) {
  return classes.filter(Boolean).join(" ");
}

export const MGPage = ({
  children,
  scrollToTopKey,
  toolbar,
  dataTestId,
  className,
  bodyClassName,
  contentClassName,
  fixedToolbarOffset,
  isMobileAuth,
  style,
}: MGPageProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);

  useFocusedFieldScroll(bodyRef, isMobileAuth);

  useScrollToTop("auto", [scrollToTopKey], isMobileAuth ? bodyRef : undefined);

  useEffect(() => {
    if (!isMobileAuth) return;

    const roots = [document.documentElement, document.body];
    roots.forEach((root) => root.classList.add(styles.mobileViewport));

    return () => roots.forEach((root) => root.classList.remove(styles.mobileViewport));
  }, [isMobileAuth]);

  const isToolbarFixed = Boolean(toolbar && toolbar.isFixed !== false);

  const shouldShowProgress =
    Boolean(toolbar) && toolbar!.currentStep !== undefined && toolbar!.totalSteps !== undefined;

  const progressHeight =
    toolbar && toolbar.progressHeight !== undefined ? toolbar.progressHeight : 4;

  const toolbarOffset =
    fixedToolbarOffset !== undefined
      ? fixedToolbarOffset
      : TOOLBAR_HEIGHT + (shouldShowProgress ? progressHeight : 0);

  const pageStyle = isToolbarFixed ? { ...style, paddingTop: toolbarOffset } : style;

  const toolbarElement = toolbar ? (
    <MGPageToolbar
      {...toolbar}
      dataTestId={toolbar.dataTestId || (dataTestId && `${dataTestId}-toolbar`)}
      isFixed={isToolbarFixed}
    />
  ) : null;

  return (
    <div
      className={getClassName(styles.page, className)}
      style={pageStyle}
      data-scroll-mode={isMobileAuth ? "internal" : "document"}
      data-testid={dataTestId}>
      {isToolbarFixed && toolbarElement}

      <div ref={bodyRef} className={getClassName(styles.body, bodyClassName)}>
        {!isToolbarFixed && toolbarElement}

        <div className={getClassName(styles.content, contentClassName)}>{children}</div>
      </div>
    </div>
  );
};
